import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import {
  Briefcase,
  Plus,
  Search,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  Eye,
  Edit2,
  Trash2,
} from 'lucide-react';

interface Job {
  id: string;
  job_number: string;
  customer?: string;
  part_number?: string;
  description?: string;
  status: 'active' | 'completed' | 'cancelled';
  created_at: string;
  completed_at?: string;
  total_tool_cost?: number;
  total_chip_time?: number;
  usage_count?: number;
}

interface JobsResponse {
  items: Job[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function Jobs() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [editingJob, setEditingJob] = useState<Job | null>(null);

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs', page, search, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: '25',
        status: statusFilter,
      });
      if (search) params.set('search', search);

      const res = await api.get<JobsResponse>(`/jobs?${params}`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success"><Play className="h-3 w-3 mr-1" />Active</Badge>;
      case 'completed':
        return <Badge variant="info"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'cancelled':
        return <Badge variant="neutral"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Jobs</h1>
          <p className="text-foreground-muted">Track tool usage and costs per job</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="h-4 w-4" />
          New Job
        </Button>
      </div>

      <Card>
        <CardBody>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
                <Input
                  placeholder="Search jobs..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="w-40">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Jobs List */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="spinner h-8 w-8 mx-auto" />
            </div>
          ) : jobsData?.items.length === 0 ? (
            <div className="text-center py-12 text-foreground-muted">
              <Briefcase className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No jobs found</p>
              <p className="text-sm">
                {search || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Create your first job to start tracking tool usage'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Job #</th>
                      <th>Customer</th>
                      <th>Part #</th>
                      <th>Status</th>
                      <th className="text-right">Tool Cost</th>
                      <th className="text-right">Chip Time</th>
                      <th className="text-right">Uses</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobsData?.items.map((job) => (
                      <tr key={job.id}>
                        <td className="font-medium">{job.job_number}</td>
                        <td>{job.customer || '-'}</td>
                        <td className="font-mono text-sm">{job.part_number || '-'}</td>
                        <td>{getStatusBadge(job.status)}</td>
                        <td className="text-right">
                          <span className="flex items-center justify-end gap-1">
                            <DollarSign className="h-3 w-3 text-foreground-muted" />
                            {(job.total_tool_cost || 0).toFixed(2)}
                          </span>
                        </td>
                        <td className="text-right">
                          <span className="flex items-center justify-end gap-1">
                            <Clock className="h-3 w-3 text-foreground-muted" />
                            {(job.total_chip_time || 0).toFixed(1)}h
                          </span>
                        </td>
                        <td className="text-right">{job.usage_count || 0}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedJob(job)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingJob(job)}
                              title="Edit"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {jobsData && jobsData.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-foreground-muted">
                    Showing {((page - 1) * 25) + 1} to {Math.min(page * 25, jobsData.total)} of {jobsData.total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={page >= jobsData.totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Add Job Modal */}
      <AddJobModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          queryClient.invalidateQueries({ queryKey: ['jobs'] });
        }}
      />

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSuccess={() => {
            setEditingJob(null);
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
          }}
        />
      )}

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onUpdate={() => {
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
          }}
        />
      )}
    </div>
  );
}

// Add Job Modal
interface AddJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function AddJobModal({ isOpen, onClose, onSuccess }: AddJobModalProps) {
  const [formData, setFormData] = useState({
    jobNumber: '',
    customer: '',
    partNumber: '',
    description: '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.post('/jobs', {
        jobNumber: data.jobNumber,
        customer: data.customer || undefined,
        partNumber: data.partNumber || undefined,
        description: data.description || undefined,
      });
      if (!res.success) throw new Error(res.error || 'Failed to create job');
      return res.data;
    },
    onSuccess: () => {
      setFormData({ jobNumber: '', customer: '', partNumber: '', description: '' });
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create job');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.jobNumber.trim()) {
      setError('Job number is required');
      return;
    }
    mutation.mutate(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Job"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            <Plus className="h-4 w-4" />
            Create Job
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <Input
          label="Job Number *"
          value={formData.jobNumber}
          onChange={(e) => setFormData({ ...formData, jobNumber: e.target.value })}
          placeholder="e.g., JOB-2024-001"
        />

        <Input
          label="Customer"
          value={formData.customer}
          onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
          placeholder="Customer name"
        />

        <Input
          label="Part Number"
          value={formData.partNumber}
          onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
          placeholder="Part number being manufactured"
        />

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Job description or notes..."
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={3}
          />
        </div>
      </form>
    </Modal>
  );
}

// Edit Job Modal
interface EditJobModalProps {
  job: Job;
  onClose: () => void;
  onSuccess: () => void;
}

function EditJobModal({ job, onClose, onSuccess }: EditJobModalProps) {
  const [formData, setFormData] = useState({
    jobNumber: job.job_number,
    customer: job.customer || '',
    partNumber: job.part_number || '',
    description: job.description || '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await api.put(`/jobs/${job.id}`, {
        jobNumber: data.jobNumber,
        customer: data.customer || undefined,
        partNumber: data.partNumber || undefined,
        description: data.description || undefined,
      });
      if (!res.success) throw new Error(res.error || 'Failed to update job');
      return res.data;
    },
    onSuccess: () => {
      setError('');
      onSuccess();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update job');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.jobNumber.trim()) {
      setError('Job number is required');
      return;
    }
    mutation.mutate(formData);
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Job"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Save Changes
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-error bg-error/10 rounded-lg">{error}</div>
        )}

        <Input
          label="Job Number *"
          value={formData.jobNumber}
          onChange={(e) => setFormData({ ...formData, jobNumber: e.target.value })}
        />

        <Input
          label="Customer"
          value={formData.customer}
          onChange={(e) => setFormData({ ...formData, customer: e.target.value })}
        />

        <Input
          label="Part Number"
          value={formData.partNumber}
          onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
        />

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            rows={3}
          />
        </div>
      </form>
    </Modal>
  );
}

// Job Detail Modal
interface JobDetailModalProps {
  job: Job;
  onClose: () => void;
  onUpdate: () => void;
}

interface ToolUsage {
  definition_id: string;
  tool_name: string;
  part_number?: string;
  total_quantity: number;
  total_chip_time: number;
  total_cost: number;
}

interface JobDetail extends Job {
  toolUsage: ToolUsage[];
  totalToolCost: number;
  totalChipTime: number;
  usageCount: number;
}

function JobDetailModal({ job, onClose, onUpdate }: JobDetailModalProps) {
  const queryClient = useQueryClient();

  const { data: jobDetail, isLoading } = useQuery({
    queryKey: ['job', job.id],
    queryFn: async () => {
      const res = await api.get<JobDetail>(`/jobs/${job.id}`);
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      const res = await api.patch(`/jobs/${job.id}/status`, { status });
      if (!res.success) throw new Error(res.error || 'Failed to update status');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', job.id] });
      onUpdate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete(`/jobs/${job.id}`);
      if (!res.success) throw new Error(res.error || 'Failed to delete job');
      return res.data;
    },
    onSuccess: () => {
      onClose();
      onUpdate();
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success"><Play className="h-3 w-3 mr-1" />Active</Badge>;
      case 'completed':
        return <Badge variant="info"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'cancelled':
        return <Badge variant="neutral"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Job: ${job.job_number}`}
      size="xl"
      footer={
        <>
          {job.status === 'active' && (
            <>
              <Button
                variant="secondary"
                onClick={() => statusMutation.mutate('completed')}
                loading={statusMutation.isPending}
              >
                <CheckCircle className="h-4 w-4" />
                Mark Complete
              </Button>
              <Button
                variant="danger"
                onClick={() => statusMutation.mutate('cancelled')}
                loading={statusMutation.isPending}
              >
                <XCircle className="h-4 w-4" />
                Cancel Job
              </Button>
            </>
          )}
          {job.status !== 'active' && (
            <Button
              variant="secondary"
              onClick={() => statusMutation.mutate('active')}
              loading={statusMutation.isPending}
            >
              <Play className="h-4 w-4" />
              Reactivate
            </Button>
          )}
          {(!jobDetail?.usageCount || jobDetail.usageCount === 0) && (
            <Button
              variant="danger"
              onClick={() => {
                if (confirm('Are you sure you want to delete this job?')) {
                  deleteMutation.mutate();
                }
              }}
              loading={deleteMutation.isPending}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </>
      }
    >
      {isLoading ? (
        <div className="text-center py-8">
          <div className="spinner h-8 w-8 mx-auto" />
        </div>
      ) : jobDetail ? (
        <div className="space-y-6">
          {/* Job Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-foreground-muted uppercase tracking-wide">Customer</p>
              <p className="font-medium">{jobDetail.customer || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-muted uppercase tracking-wide">Part Number</p>
              <p className="font-medium font-mono">{jobDetail.part_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-muted uppercase tracking-wide">Status</p>
              <div className="mt-1">{getStatusBadge(jobDetail.status)}</div>
            </div>
            <div>
              <p className="text-xs text-foreground-muted uppercase tracking-wide">Created</p>
              <p className="font-medium">{new Date(jobDetail.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {jobDetail.description && (
            <div>
              <p className="text-xs text-foreground-muted uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm">{jobDetail.description}</p>
            </div>
          )}

          {/* Cost Summary */}
          <div className="border-t border-border pt-4">
            <h3 className="font-semibold mb-3">Cost Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-surface-hover rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-success">${(jobDetail.totalToolCost || 0).toFixed(2)}</p>
                <p className="text-xs text-foreground-muted">Total Tool Cost</p>
              </div>
              <div className="bg-surface-hover rounded-lg p-3 text-center">
                <p className="text-2xl font-bold">{(jobDetail.totalChipTime || 0).toFixed(1)}h</p>
                <p className="text-xs text-foreground-muted">Total Chip Time</p>
              </div>
              <div className="bg-surface-hover rounded-lg p-3 text-center">
                <p className="text-2xl font-bold">{jobDetail.usageCount || 0}</p>
                <p className="text-xs text-foreground-muted">Tool Uses</p>
              </div>
            </div>
          </div>

          {/* Tool Usage Breakdown */}
          {jobDetail.toolUsage && jobDetail.toolUsage.length > 0 && (
            <div className="border-t border-border pt-4">
              <h3 className="font-semibold mb-3">Tool Usage Breakdown</h3>
              <div className="max-h-60 overflow-y-auto border border-border rounded-lg">
                <table className="table text-sm">
                  <thead className="sticky top-0 bg-surface">
                    <tr>
                      <th>Tool</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Chip Time</th>
                      <th className="text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobDetail.toolUsage.map((usage) => (
                      <tr key={usage.definition_id}>
                        <td>
                          <div>
                            <p className="font-medium">{usage.tool_name}</p>
                            {usage.part_number && (
                              <p className="text-xs text-foreground-muted font-mono">{usage.part_number}</p>
                            )}
                          </div>
                        </td>
                        <td className="text-right">{usage.total_quantity}</td>
                        <td className="text-right">{usage.total_chip_time.toFixed(1)}h</td>
                        <td className="text-right font-medium">${usage.total_cost.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!jobDetail.toolUsage || jobDetail.toolUsage.length === 0) && (
            <div className="border-t border-border pt-4">
              <div className="text-center py-6 text-foreground-muted border border-border border-dashed rounded-lg">
                <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No tool usage recorded yet</p>
                <p className="text-xs">Check out tools and assign them to this job</p>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </Modal>
  );
}
